// 认证 UI 快照缓存 / Auth UI snapshot cache.
// 只保存首屏展示所需的非敏感字段；真实权限仍以后端 session cookie 为准。
// Stores only non-sensitive first-paint UI fields; real authorization still
// depends on the backend session cookie.

import type { AuthStatus } from "@/api/auth";

const AUTH_CACHE_KEY = "ripple.auth.snapshot.v1";
const CACHE_VERSION = 1;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CachedAuthSnapshot {
  version: typeof CACHE_VERSION;
  savedAt: number;
  email: string;
  credit: number;
  preferred_language: "cn" | "en";
}

/**
 * 读取缓存认证快照 / Read the cached auth snapshot.
 */
export function readCachedAuthStatus(): AuthStatus | null {
  const storage = _storage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedAuthSnapshot>;
    if (
      parsed.version !== CACHE_VERSION ||
      typeof parsed.savedAt !== "number" ||
      Date.now() - parsed.savedAt > CACHE_TTL_MS ||
      typeof parsed.email !== "string" ||
      parsed.email.trim() === "" ||
      typeof parsed.credit !== "number" ||
      !isLanguage(parsed.preferred_language)
    ) {
      clearCachedAuthStatus();
      return null;
    }
    return _snapshotToStatus(parsed as CachedAuthSnapshot);
  } catch {
    clearCachedAuthStatus();
    return null;
  }
}

/**
 * 按真实 AuthStatus 更新缓存 / Update cache from an authoritative AuthStatus.
 */
export function writeCachedAuthStatus(status: AuthStatus): void {
  const storage = _storage();
  if (!storage) return;
  if (!status.authenticated || !status.user) {
    clearCachedAuthStatus();
    return;
  }
  const snapshot: CachedAuthSnapshot = {
    version: CACHE_VERSION,
    savedAt: Date.now(),
    email: status.user.email,
    credit: status.user.credit,
    preferred_language: status.user.preferred_language,
  };
  try {
    storage.setItem(AUTH_CACHE_KEY, JSON.stringify(snapshot));
  } catch {
    // 忽略缓存写入失败 / Ignore cache write failures.
  }
}

/**
 * 清除认证快照缓存 / Clear the cached auth snapshot.
 */
export function clearCachedAuthStatus(): void {
  const storage = _storage();
  if (!storage) return;
  try {
    storage.removeItem(AUTH_CACHE_KEY);
  } catch {
    // 忽略缓存清理失败 / Ignore cache cleanup failures.
  }
}

function _snapshotToStatus(snapshot: CachedAuthSnapshot): AuthStatus {
  return {
    authenticated: true,
    provider: "local",
    user: {
      customer_id: "cached",
      email: snapshot.email,
      credit: snapshot.credit,
      is_verified: true,
      preferred_language: snapshot.preferred_language,
      created_at: null,
    },
    capabilities: {
      register: true,
      password_reset: true,
      change_password: true,
      logout: true,
    },
  };
}

function _storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// 校验缓存语言值 / Validate cached language values.
function isLanguage(value: unknown): value is "cn" | "en" {
  return value === "cn" || value === "en";
}
